"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { scheduleIdle } from "@/lib/scheduleIdle";

export type Currency = "USD" | "EUR" | "GBP";

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
};

const STORAGE_KEY = "zcashtracker-currency";

interface CurrencyContextType {
  /** Currently selected currency */
  currency: Currency;
  /** Switch the active currency (persists to localStorage) */
  setCurrency: (c: Currency) => void;
  /** Symbol for the active currency (e.g. "$", "€", "£") */
  currencySymbol: string;
  /** Multiply a USD value by this to get the value in the active currency */
  exchangeRate: number;
  /** Convenience: convert a USD amount to the active currency */
  convertUsd: (usdAmount: number) => number;
  /** Whether rates are still being fetched for the first time */
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined,
);

// ExchangeRate-API open-access endpoint (no key required, updates daily)
// https://www.exchangerate-api.com/docs/free
const RATES_URL = "/api/exchange-rates";

// Fallback rates in case the API is unreachable (updated 2026-03-05)
const FALLBACK_RATES: Record<Currency, number> = {
  USD: 1,
  EUR: 0.861,
  GBP: 0.75,
};

export function CurrencyProvider({
  children,
}: {
  children: ReactNode;
}): React.ReactElement {
  const [currency, setCurrencyState] = useState<Currency>("USD");
  const [rates, setRates] = useState<Record<Currency, number>>(FALLBACK_RATES);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate from localStorage (only on client)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && ["USD", "EUR", "GBP"].includes(stored)) {
        setCurrencyState(stored as Currency);
      }
    } catch {
      // SSR or storage unavailable – ignore
    }
  }, []);

  // Persist selection
  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // ignore
    }
  }, []);

  // Fetch exchange rates once on mount, then every 10 minutes
  useEffect(() => {
    let cancelled = false;

    const fetchRates = async () => {
      try {
        const res = await fetch(RATES_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data.USD && data.EUR && data.GBP) {
          setRates({
            USD: data.USD,
            EUR: data.EUR,
            GBP: data.GBP,
          });
        }
      } catch (err) {
        console.warn("Failed to fetch exchange rates, using fallback:", err);
        // keep whatever rates we already have
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    let interval: ReturnType<typeof setInterval> | undefined;
    const cancelIdle = scheduleIdle(() => {
      void fetchRates();
      interval = setInterval(fetchRates, 10 * 60 * 1000);
    }, 2000);
    return () => {
      cancelled = true;
      cancelIdle();
      if (interval) clearInterval(interval);
    };
  }, []);

  const exchangeRate = rates[currency];

  const convertUsd = useCallback(
    (usdAmount: number) => usdAmount * rates[currency],
    [rates, currency],
  );

  const currencySymbol = CURRENCY_SYMBOLS[currency];

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        currencySymbol,
        exchangeRate,
        convertUsd,
        isLoading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextType {
  const ctx = useContext(CurrencyContext);
  if (ctx === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return ctx;
}

export { CURRENCY_SYMBOLS };
